import React, { useEffect, useMemo, useState } from "react";
import { useEditorStore } from "../../../../../state/editorStore";
import { generateOutlineDataUrl } from "../../../../../utils/generateOutlineImage";
import { getTransform } from "../../../../../utils/getTransform";
import {
  OUTLINE_ENTRY_BACKGROUND,
  TOOL_PREP_PREVIEW_BG,
} from "../../../../../utils/editorEntrySession";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./OutlinePreview.css";

/**
 * Prep preview: after BG remove, subject box hugs opaque cutout (Editor-like)
 * with move / resize on BB + handles.
 */
const OutlinePreview = ({
  imageUrl = null,
  uploadedImageUrl = null,
  loading = false,
  applying = false,
  applyingLabel = "Applying outline…",
  previewBg = TOOL_PREP_PREVIEW_BG.WHITE,
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
  onSubjectPlacement = null,
}) => {
  const outline = useEditorStore((s) => s.object.outline);
  const naturalSize = useImageNaturalSize(imageUrl || uploadedImageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const [outlineImageUrl, setOutlineImageUrl] = useState(null);
  const [outlineBleedPx, setOutlineBleedPx] = useState(0);
  const stageRef = React.useRef(null);
  const genTokenRef = React.useRef(0);

  const isTransparent = previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const isOriginal = previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL;
  const isEmpty = !imageUrl && !uploadedImageUrl && !loading;
  const useChecker = isTransparent || isEmpty;

  const imageAspect = useMemo(
    () => aspectFromSize(naturalSize.width, naturalSize.height, 1),
    [naturalSize]
  );

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        imageAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (!fitted) return;
      setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageAspect, imageUrl]);

  useEffect(() => {
    if (!imageUrl || !outline?.enabled || !(outline.width > 0)) {
      setOutlineImageUrl(null);
      setOutlineBleedPx(0);
      return undefined;
    }

    const token = ++genTokenRef.current;
    let cancelled = false;
    const timer = setTimeout(() => {
      generateOutlineDataUrl(imageUrl, {
        width: outline.width,
        color: outline.color,
      })
        .then((result) => {
          if (cancelled || token !== genTokenRef.current) return;
          setOutlineImageUrl(result?.dataUrl || null);
          setOutlineBleedPx(Number(result?.bleedPx) || 0);
        })
        .catch(() => {
          if (!cancelled && token === genTokenRef.current) {
            setOutlineImageUrl(null);
            setOutlineBleedPx(0);
          }
        });
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [imageUrl, outline?.enabled, outline?.width, outline?.color]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  const fitFrame = useMemo(() => {
    if (!(transform.width > 0)) return null;
    return {
      x: transform.x,
      y: transform.y,
      width: transform.width,
      height: transform.height,
    };
  }, [transform.x, transform.y, transform.width, transform.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading && !applying;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
      enabled: interactive,
      imageUrl,
      fitFrame,
    resetKey: imageUrl,
  });

  useReportSubjectPlacement(
    onSubjectPlacement,
    object,
    contentCrop,
    stageSize,
    imageUrl
  );

  const showOutline =
    outline?.enabled && outline.width > 0 && Boolean(outlineImageUrl);
  const blurPx = Math.max(0, Number(outline?.blur) || 0);

  // Outline stroke can paint outside the opaque crop (same as Editor).
  const outlineStyle = (() => {
    if (!object || !naturalSize.width || !contentCrop) {
      if (!object) return undefined;
      const bleed = Math.max(0, Number(outlineBleedPx) || 0);
      if (bleed <= 0 || !naturalSize.width) {
        return { ...imageStyle, filter: blurPx > 0 ? `blur(${blurPx}px)` : "none" };
      }
    }
    if (!object || !contentCrop || !naturalSize.width) {
      return imageStyle
        ? { ...imageStyle, filter: blurPx > 0 ? `blur(${blurPx}px)` : "none" }
        : undefined;
    }
    const bleed = Math.max(0, Number(outlineBleedPx) || 0);
    const cropW = contentCrop.width;
    const cropH = contentCrop.height;
    const fullW = naturalSize.width + 2 * bleed;
    const fullH = naturalSize.height + 2 * bleed;
    return {
      position: "absolute",
      objectFit: "fill",
      maxWidth: "none",
      maxHeight: "none",
      width: cropW > 0 && fullW > 0 ? `${(fullW / cropW) * 100}%` : "100%",
      height: cropH > 0 && fullH > 0 ? `${(fullH / cropH) * 100}%` : "100%",
      left:
        cropW > 0
          ? `${-((contentCrop.x + bleed) / cropW) * 100}%`
          : "0",
      top:
        cropH > 0
          ? `${-((contentCrop.y + bleed) / cropH) * 100}%`
          : "0",
      filter: blurPx > 0 ? `blur(${blurPx}px)` : "none",
      clipPath: "none",
    };
  })();

  const fallbackFitStyle = fitFrame
    ? {
        left: fitFrame.x,
        top: fitFrame.y,
        width: fitFrame.width,
        height: fitFrame.height,
      }
    : undefined;

  return (
    <ToolPrepCanvasStage
      className="outline-preview"
      ref={stageRef}
      metaImageUrl={imageUrl || uploadedImageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      <div
        className={`outline-preview__stage${
          useChecker ? " outline-preview__stage--checker" : ""
        }${isOriginal && !isEmpty ? " outline-preview__stage--original" : ""}`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor:
            useChecker || isOriginal ? undefined : OUTLINE_ENTRY_BACKGROUND,
        }}
      >
        <div className="tool-prep-stage__clip outline-preview__pixels">
          {isOriginal && uploadedImageUrl ? (
            <img
              src={uploadedImageUrl}
              alt=""
              className={
                fitFrame
                  ? "outline-preview__uploaded-bg"
                  : "outline-preview__uploaded-bg outline-preview__uploaded-bg--solo"
              }
              style={fallbackFitStyle}
              draggable={false}
            />
          ) : null}
          {loading ? (
            <div className="outline-preview__loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : applying ? (
            <div className="outline-preview__loading">
              <RoundLoader size="md" label={applyingLabel} />
            </div>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="outline-preview__subject"
              style={subjectWrapperStyleForCrop(
                { ...wrapperStyle, overflow: "visible" },
                isCropping
              )}
            >
              {showOutline ? (
                <img
                  src={outlineImageUrl}
                  alt=""
                  className="outline-preview__outline"
                  style={outlineStyle}
                  draggable={false}
                />
              ) : null}
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={imageStyle}
              />
              <img
                src={imageUrl}
                alt="Outline preview"
                className="outline-preview__image"
                style={subjectMainImageStyleForCrop(imageStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <div className="outline-preview__stack">
              {showOutline ? (
                <img
                  src={outlineImageUrl}
                  alt=""
                  className={
                    fitFrame
                      ? "outline-preview__outline"
                      : "outline-preview__outline outline-preview__outline--fallback"
                  }
                  style={
                    fitFrame
                      ? {
                          ...fallbackFitStyle,
                          filter: blurPx > 0 ? `blur(${blurPx}px)` : "none",
                        }
                      : { filter: blurPx > 0 ? `blur(${blurPx}px)` : "none" }
                  }
                  draggable={false}
                />
              ) : null}
              <img
                src={imageUrl}
                alt="Outline preview"
                className={
                  fitFrame
                    ? "outline-preview__image"
                    : "outline-preview__image outline-preview__image--fallback"
                }
                style={fallbackFitStyle}
                draggable={false}
              />
            </div>
          ) : (
            <div className="outline-preview__empty">{emptyLabel}</div>
          )}
        </div>
        {imageUrl && interactive && object && wrapperStyle ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default OutlinePreview;
